<template>
  <div class="bg-surface-default-low flex flex-col gap-32 p-24">
    <div class="flex justify-between gap-32">
      <div class="flex flex-col gap-16">
        <div class="flex flex-col">
          <h2 class="heading-2">
            {{
              i18n(translations.numberOfOpportunities, {
                numberOfOpportunities,
              })
            }}
          </h2>
          <p class="body-2">{{ i18n(translations.description) }}</p>
        </div>

        <div class="flex gap-4">
          <IconInfo />
          <span class="body-2">{{
            i18n(translations.lastUpdatedAt, { lastUpdatedAt })
          }}</span>
        </div>
      </div>
      <div>
        <RevInputSelect
          id="page-size"
          v-model="pageSizeInput"
          class="w-160"
          :label="i18n(translations.pageSize)"
          :options="pageSizeOptions"
          @update:model-value="handlePageSizeChange"
        />
      </div>
    </div>

    <div v-if="isLoading">
      <RevLoadingScreen class="h-640" :text="i18n(translations.loading)" />
    </div>
    <RevTable
      v-else
      :collection="[
        ...opportunities,
        ...Array(10).fill({
          profitPotentialRank: 2,
          productId: 2,
          productTitle: 'Product Title 2',
          grade: 'Grade 2',
          marketCode: 'FR',
          priceToWinBackboxEstimation: { amount: 100, currency: 'EUR' },
          margin: { amount: 20, currency: 'EUR' },
          action: 'Create listing',
          competitionLevel: 'HIGH',
          ordersVolume: '100',
          averageSellingPrice: { amount: 100, currency: 'EUR' },
        }),
      ]"
      :columns="formattedColumns"
      :sort-options
      transparent-header
      @update:sort-options="handleSortOptionChanged"
    >
      <template #body-grade="{ item }">
        <RevTag :label="item.grade" variant="secondary" />
      </template>
      <template #body-marketCode="{ item }">
        <Flag :country-code="item.marketCode" size="l" />
      </template>
      <template #body-priceToWinBackboxEstimation="{ item }">
        {{ i18n.price(item.priceToWinBackboxEstimation) }}
      </template>
      <template #body-margin="{ item }">{{ i18n.price(item.margin) }}</template>
      <template #body-averageSellingPrice="{ item }">
        {{ i18n.price(item.averageSellingPrice) }}
      </template>
      <template #body-competitionLevel="{ item }">
        <RevTag
          :label="getLabelFromCompetitionLevel(item.competitionLevel)"
          :variant="getVariantFromCompetitionLevel(item.competitionLevel)"
        />
      </template>
      <template #body-action="{ item }">
        <RevButton
          variant="primary"
          @click="handleCreateListing(item.productId)"
        >
          {{ getActionLabel(item.action) }}
        </RevButton>
      </template>
    </RevTable>

    <Pagination
      :current-page="pageNumberInput"
      :page-count="pageCount + 10"
      @click="handlePagination"
    />
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevInputSelect, type Value } from '@ds/components/InputSelect'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTable, type SortOptions } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconInfo } from '@ds/icons/IconInfo'

import {
  ACTIONS,
  COMPETITION_LEVELS,
  ORDER_BY,
  type SourcingOpportunity,
} from '~/api/quotes/types'
import Pagination from '~/components/Pagination/Pagination.vue'
import { ROUTE_NAMES } from '~/constants/routes'

import { useOpportunitiesFilters } from '../composables/useOpportunitiesFilters'

import translations from './OpportunitiesTable.translations'

const i18n = useI18n()
const { trackClick } = useTracking()

defineProps<{
  opportunities: SourcingOpportunity[]
  pageCount: number
  lastUpdatedAt: string
  numberOfOpportunities: number
  isLoading: boolean
}>()

const ascendingInput = defineModel<boolean>('ascendingInput', {
  required: true,
})
const orderByInput = defineModel<string>('orderByInput', { required: true })
const pageNumberInput = defineModel<number>('pageNumberInput', {
  required: true,
})
const pageSizeInput = defineModel<string>('pageSizeInput', { required: true })

const { pageSizeOptions } = useOpportunitiesFilters()

function getOrderEnumByKey(key: string) {
  switch (key) {
    case 'profitPotentialRank':
      return ORDER_BY.PROFIT_POTENTIAL
    case 'priceToWinBackboxEstimation':
      return ORDER_BY.PRICE_TO_WIN
    case 'margin':
      return ORDER_BY.MARGIN
    case 'averageSellingPrice':
      return ORDER_BY.SELLING_PRICE
    case 'ordersVolume':
      return ORDER_BY.ORDER_VOLUME
    case 'competitionLevel':
      return ORDER_BY.COMPETITORS_COUNT
    default:
      return key
  }
}

function getOrderKeyByEnum(enumValue: string) {
  switch (enumValue) {
    case ORDER_BY.PROFIT_POTENTIAL:
      return 'profitPotentialRank'
    case ORDER_BY.PRICE_TO_WIN:
      return 'priceToWinBackboxEstimation'
    case ORDER_BY.MARGIN:
      return 'margin'
    case ORDER_BY.SELLING_PRICE:
      return 'averageSellingPrice'
    case ORDER_BY.ORDER_VOLUME:
      return 'ordersVolume'
    case ORDER_BY.COMPETITORS_COUNT:
      return 'competitionLevel'
    default:
      return enumValue
  }
}

const sortOptions = computed<SortOptions>(() => ({
  sortBy: getOrderKeyByEnum(orderByInput.value),
  sortOrder: ascendingInput.value ? 'ASC' : 'DESC',
}))

const formattedColumns = [
  {
    key: 'profitPotentialRank',
    label: i18n(translations.profitPotentialRank),
    isSortable: true,
  },
  { key: 'productTitle', label: i18n(translations.productTitle) },
  { key: 'grade', label: i18n(translations.grade) },
  { key: 'marketCode', label: i18n(translations.marketCode) },
  {
    key: 'priceToWinBackboxEstimation',
    label: i18n(translations.priceToWinBackboxEstimation),
    isSortable: true,
  },
  {
    key: 'margin',
    label: i18n(translations.margin),
    isSortable: true,
    highlighted: true,
  },
  {
    key: 'averageSellingPrice',
    label: i18n(translations.averageSellingPrice),
    isSortable: true,
  },
  {
    key: 'ordersVolume',
    label: i18n(translations.ordersVolume),
    isSortable: true,
  },
  {
    key: 'competitionLevel',
    label: i18n(translations.competitionLevel),
    isSortable: true,
  },
  {
    key: 'action',
    label: '',
  },
]

function handlePagination({ page }: { page: number }) {
  trackClick({
    name: 'navigate_to_page',
    zone: 'opportunities_filters',
    value: page,
  })

  pageNumberInput.value = page
}

function handlePageSizeChange(value: Value) {
  if (typeof value === 'string') {
    trackClick({
      name: 'change_page_size',
      zone: 'opportunities_filters',
      value,
    })
  }
}

function handleSortOptionChanged(options: SortOptions) {
  ascendingInput.value = options.sortOrder === 'ASC'
  orderByInput.value = getOrderEnumByKey(options.sortBy)

  trackClick({
    name: 'sort_opportunities',
    zone: 'opportunities_filters',
    value: options.sortBy,
  })
}

function handleCreateListing(productId: number) {
  navigateTo({
    name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
    params: { productId },
  })

  trackClick({
    name: 'create_listing',
    zone: 'opportunities_filters',
    value: productId,
  })
}

function getActionLabel(action: string) {
  switch (action) {
    case ACTIONS.CREATE_LISTING:
      return i18n(translations.createListing)
    case ACTIONS.TURN_ONLINE:
      return i18n(translations.turnOnline)
    case ACTIONS.UPDATE_PRICE:
    default:
      return i18n(translations.updatePrice)
  }
}

function getLabelFromCompetitionLevel(competitionLevel: string) {
  switch (competitionLevel) {
    case COMPETITION_LEVELS.HIGH:
      return i18n(translations.high)
    case COMPETITION_LEVELS.MEDIUM:
      return i18n(translations.medium)
    case COMPETITION_LEVELS.LOW:
      return i18n(translations.low)
    case COMPETITION_LEVELS.VERY_LOW:
    default:
      return i18n(translations.veryLow)
  }
}

function getVariantFromCompetitionLevel(competitionLevel: string) {
  switch (competitionLevel) {
    case COMPETITION_LEVELS.HIGH:
      return 'danger'
    case COMPETITION_LEVELS.MEDIUM:
      return 'warning'
    case COMPETITION_LEVELS.LOW:
      return 'success'
    case COMPETITION_LEVELS.VERY_LOW:
    default:
      return 'info'
  }
}
</script>
